// BackOfficeDashboard.tsx (Cod Final)

import { useEffect, useState } from "react";
import { api } from "../../lib/api";
// Importăm noul header
// import BackOfficeHeader from "../ui/BackOfficeHeader";
import LayoutBackOffice from "../ui/LayoutBackOfficeSidebar";

import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import QRCode from "qrcode";

// ... (Interfețele rămân aceleași) ...

interface HotelStats {
  totalRooms: number;
  occupiedRooms: number;
  monthlyRevenue: number;
  avgDailyRate: number;
  occupancyRate: number;
}

export default function BackOfficeDashboard() {
  const [stats, setStats] = useState<HotelStats | null>(null);
  const [chartData, setChartData] = useState<
    { month: string; revenue: number; occupancy: number }[]
  >([]);

  // USERNAME și logica de autentificare sunt acum gestionate de BackOfficeHeader
  // Dar păstrăm logica de Logout aici pentru a evita dependența circulară,
  // deși Header-ul tău are deja funcția logout

  useEffect(() => {
    // ... (Logica de preluare date/mock rămâne aceeași) ...
    (async () => {
      try {
        const data = await api("/api/backoffice/stats");
        setStats(data);
      } catch {
        setStats({
          totalRooms: 60,
          occupiedRooms: 48,
          monthlyRevenue: 18750,
          avgDailyRate: 78,
          occupancyRate: 80,
        });
      }

      setChartData([
        { month: "Jan", revenue: 12000, occupancy: 65 },
        { month: "Feb", revenue: 13500, occupancy: 68 },
        { month: "Mar", revenue: 14500, occupancy: 72 },
        { month: "Apr", revenue: 16200, occupancy: 74 },
        { month: "May", revenue: 17500, occupancy: 78 },
        { month: "Jun", revenue: 18750, occupancy: 80 },
        { month: "Jul", revenue: 19800, occupancy: 83 },
        { month: "Aug", revenue: 21000, occupancy: 85 },
        { month: "Sep", revenue: 19000, occupancy: 81 },
        { month: "Oct", revenue: 17800, occupancy: 79 },
        { month: "Nov", revenue: 16500, occupancy: 75 },
        { month: "Dec", revenue: 19500, occupancy: 84 },
      ]);
    })();
  }, []);

  /* === Funcționalitate Export Excel (Mutat aici, necesită chartData) === */
  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(chartData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "RevenueData");
    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([buffer], { type: "application/octet-stream" });
    saveAs(blob, "HotelVPF_BackOffice_Data.xlsx");
  };

  /* === BASE PDF GENERATOR (Mutat aici, necesită chartData și stats) === */
  const generatePDFBase = async (mode: "light" | "dark", filename: string) => {
    // ... (Codul funcției generatePDFBase rămâne același) ...
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageCenter = pageWidth / 2;

    const isDark = mode === "dark";

    // === Background pentru Dark Mode ===
    if (isDark) {
      doc.setFillColor(18, 18, 18);
      doc.rect(0, 0, pageWidth, pageHeight, "F");
    }

    // === Logo (necesită pre-încărcarea logo-ului) ===
    const logoUrl = "/logos/hotelvpf-logo.png";
    let logoBase64: string = "";
    try {
      const response = await fetch(logoUrl);
      const blob = await response.blob();
      const reader = new FileReader();
      logoBase64 = await new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.error("Failed to load logo for PDF:", e);
    }

    // === Watermark central translucide ===
    if (logoBase64) {
      doc.saveGraphicsState();
      doc.setGState(new (doc as any).GState({ opacity: isDark ? 0.04 : 0.06 }));
      doc.addImage(
        logoBase64,
        "PNG",
        pageCenter - 50,
        pageHeight / 2 - 50,
        100,
        100
      );
      doc.restoreGraphicsState();
    }

    // === Antet "Confidential" ===
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(isDark ? 150 : 100);
    doc.text("Confidential — HotelVPF Back Office Data", pageWidth - 18, 10, {
      align: "right",
    });

    // === Header principal ===
    const today = new Date();
    const dateStr = today.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    const textColor: [number, number, number] = isDark
      ? [235, 235, 235]
      : [33, 37, 41];
    const subText: [number, number, number] = isDark
      ? [180, 180, 180]
      : [90, 90, 90];
    const accent: [number, number, number] = [37, 99, 235];

    if (logoBase64) {
      doc.addImage(logoBase64, "PNG", pageCenter - 11, 15, 22, 22);
    }

    doc.setTextColor(...textColor);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("HotelVPF Back Office Report", pageCenter, 42, {
      align: "center",
    });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(...subText);
    doc.text("Monthly Revenue & Occupancy", pageCenter, 50, {
      align: "center",
    });
    doc.setFontSize(10);
    doc.text(`Generated on: ${dateStr}`, pageCenter, 56, { align: "center" });
    doc.setDrawColor(...accent);
    doc.line(20, 60, pageWidth - 20, 60);

    // === Table ===
    autoTable(doc, {
      startY: 68,
      styles: {
        halign: "center",
        fontSize: 10,
        textColor: isDark ? [240, 240, 240] : [0, 0, 0],
        fillColor: isDark ? [25, 25, 25] : [255, 255, 255],
      },
      headStyles: {
        fillColor: accent,
        textColor: [255, 255, 255],
      },
      alternateRowStyles: {
        fillColor: isDark ? [20, 20, 20] : [245, 247, 250],
      },
      head: [["Month", "Revenue (€)", "Occupancy (%)"]],
      body: chartData.map((r) => [
        r.month,
        r.revenue.toLocaleString(),
        r.occupancy,
      ]),
    });

    // === Summary ===
    const finalY = (doc as any).lastAutoTable.finalY + 12;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(...textColor);
    doc.text("Summary", 20, finalY);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(...subText);
    doc.text(`• Total Rooms: ${stats?.totalRooms ?? 0}`, 25, finalY + 8);
    doc.text(
      `• Average Daily Rate: €${stats?.avgDailyRate ?? 0}`,
      25,
      finalY + 16
    );
    doc.text(
      `• Current Occupancy: ${stats?.occupancyRate ?? 0}%`,
      25,
      finalY + 24
    );
    doc.text(
      `• Monthly Revenue: €${stats?.monthlyRevenue?.toLocaleString() ?? 0}`,
      25,
      finalY + 32
    );

    // === Signature & QR ===
    const qrY = 230;
    const qrSize = 35;
    const leftX = pageCenter - 80;
    const rightX = pageCenter + 45;

    doc.setDrawColor(isDark ? 180 : 0);
    doc.line(leftX - 15, qrY + 20, leftX + 55, qrY + 20);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...textColor);
    doc.text("Authorized Manager", leftX + 20, qrY + 28, { align: "center" });
    doc.setFont("helvetica", "italic");
    doc.setTextColor(...subText);
    doc.text("Signature", leftX + 20, qrY + 35, { align: "center" });

    const qrDataUrl = await QRCode.toDataURL("https://hotelvpf.com");
    doc.addImage(qrDataUrl, "PNG", rightX, qrY, qrSize, qrSize);
    doc.setDrawColor(...accent);
    doc.line(
      rightX - 5,
      qrY + qrSize + 4,
      rightX + qrSize + 5,
      qrY + qrSize + 4
    );
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.setTextColor(...subText);
    doc.text(
      "Digitally Verified by HotelVPF.com",
      rightX + qrSize / 2,
      qrY + qrSize + 12,
      { align: "center" }
    );

    // === Footer ===
    const footerY = pageHeight - 5;
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(isDark ? 170 : 120);
    doc.text(
      "Generated automatically by HotelVPF SaaS System © 2025",
      pageCenter,
      footerY,
      { align: "center" }
    );

    doc.save(filename);
  };

  /* === Light / Dark Export === */
  const exportToPDFLight = () =>
    generatePDFBase("light", "HotelVPF_Report_Light.pdf");
  const exportToPDFDark = () =>
    generatePDFBase("dark", "HotelVPF_Report_Dark.pdf");

  return (
    <LayoutBackOffice
      title=""
      onExportExcel={exportToExcel}
      onExportPDFLight={exportToPDFLight}
      onExportPDFDark={exportToPDFDark}
    >
      <div
        style={{
          maxWidth: 1100,
          margin: "30px auto 0 auto",
          padding: "0 28px",
        }}
      >
        <p
          style={{
            color: "#6b7280",
            fontSize: 15,
            marginBottom: 24,
            fontFamily: "'Poppins', sans-serif",
          }}
        >
          Managerial panel — track revenue, occupancy and performance.
        </p>

        {stats ? (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 16,
                marginBottom: 28,
              }}
            >
              <StatCard title="Total Rooms" value={stats.totalRooms ?? 0} />
              <StatCard title="Occupied" value={stats.occupiedRooms ?? 0} />
              <StatCard
                title="Occupancy Rate"
                value={`${stats.occupancyRate ?? 0}%`}
              />
              <StatCard
                title="Avg Daily Rate"
                value={`€${stats.avgDailyRate ?? 0}`}
              />
              <StatCard
                title="Monthly Revenue"
                value={`€${(stats.monthlyRevenue ?? 0).toLocaleString()}`}
                highlight
              />
            </div>

            <div
              style={{
                background: "white",
                borderRadius: 12,
                padding: "24px 30px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                marginBottom: 28,
              }}
            >
              <h3
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  marginBottom: 14,
                  color: "#111827",
                }}
              >
                📈 Monthly Revenue & Occupancy Trends
              </h3>

              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" stroke="#6b7280" />
                  <YAxis yAxisId="left" stroke="#2563eb" />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    stroke="#f59e0b"
                    domain={[50, 100]}
                  />
                  <Tooltip />
                  <Legend />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="revenue"
                    stroke="#2563eb"
                    strokeWidth={2}
                    name="Revenue (€)"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="occupancy"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    name="Occupancy (%)"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        ) : (
          <p>Loading data...</p>
        )}
      </div>
    </LayoutBackOffice>
  );
}

// Componenta auxiliară StatCard (rămâne neschimbată)
function StatCard({
  title,
  value,
  highlight,
}: {
  title: string;
  value: any;
  highlight?: boolean;
}) {
  return (
    <div
      style={{
        background: highlight ? "#2563eb" : "white",
        color: highlight ? "white" : "#111827",
        padding: "20px",
        borderRadius: 12,
        boxShadow: highlight
          ? "0 3px 10px rgba(37,99,235,0.25)"
          : "0 1px 5px rgba(0,0,0,0.06)",
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 13, color: highlight ? "#bfdbfe" : "#6b7280" }}>
        {title}
      </div>
      <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>
        {value !== undefined && value !== null ? value.toString() : "-"}
      </div>
    </div>
  );
}
